import { Banknote } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import BlocoRecolhivel from "./BlocoRecolhivel";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}`, "Content-Type": "application/json" });

const ATALHOS = [0, 0.5, 1, 2, 5];
const reais = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const paraCampo = (v) => Number(v || 0).toFixed(2).replace(".", ",");
// "1,50" (com vírgula: ponto é milhar) ou "1.5" (sem vírgula: ponto é decimal)
const lerReais = (v) => {
  if (typeof v === "number") return v;
  const s = String(v).trim();
  if (!s) return NaN;
  return Number(s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s);
};

/** Caixa: diferença aceitável (pedido do dono, 27/10/2026). Até esse valor, para
 *  mais ou para menos em cada forma de pagamento, o fechamento conta como "bateu"
 *  e só avisa a diferença; acima dele, "não bateu" e pede o motivo. Padrão R$ 1,00. */
export default function CaixaPreferencias({ toast }) {
  const [tolerancia, setTolerancia] = useState(null); // null = carregando
  const [campo, setCampo] = useState("");
  const [salvando, setSalvando] = useState(false);
  useEffect(() => {
    fetch(`${API_URL}/api/settings/caixa-tolerancia`, { headers: auth(), cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { const v = d?.tolerancia ?? 1; setTolerancia(v); setCampo(paraCampo(v)); })
      .catch(() => { setTolerancia(1); setCampo(paraCampo(1)); });
  }, []);

  const salvar = async (valor) => {
    const n = lerReais(valor);
    if (Number.isNaN(n) || n < 0 || n > 100) {
      toast({ title: "Valor inválido", description: "Use um valor entre R$ 0,00 e R$ 100,00.", variant: "destructive" });
      return;
    }
    setSalvando(true);
    try {
      const r = await fetch(`${API_URL}/api/settings/caixa-tolerancia`, { method: "PUT", headers: auth(), body: JSON.stringify({ tolerancia: n }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Não foi possível salvar.");
      setTolerancia(d.tolerancia);
      setCampo(paraCampo(d.tolerancia));
      toast({ title: "Diferença aceitável salva", description: d.tolerancia ? `O caixa bate com até ${reais(d.tolerancia)} a mais ou a menos.` : "O caixa só bate com o valor exato." });
    } catch (e) {
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    } finally {
      setSalvando(false);
    }
  };

  const resumo = tolerancia == null ? "Carregando…" : tolerancia ? `Bate com até ${reais(tolerancia)} a mais ou a menos` : "Só bate com o valor exato";
  return (
    <BlocoRecolhivel e2e="card-caixa-tolerancia" icone={Banknote} titulo="Caixa: diferença aceitável" resumo={resumo} carregando={tolerancia == null}>
      <p className="text-sm text-muted-foreground">
        Até esse valor, para mais ou para menos em cada forma de pagamento, o fechamento do caixa conta como <b>bateu</b> e
        só mostra a diferença (ex.: "bateu, faltaram R$ 0,02"). Passou disso, o caixa <b>não bateu</b>: a tela avisa e pede o motivo.
      </p>
      <div className="mt-4 flex flex-wrap gap-2" data-e2e="caixa-tolerancia-atalhos">
        {ATALHOS.map((v) => (
          <button key={v} type="button" disabled={tolerancia == null || salvando} onClick={() => salvar(v)} aria-pressed={tolerancia === v}
            className={`h-9 px-3 rounded-lg border text-sm font-medium tabular-nums transition-colors disabled:opacity-50 ${
              tolerancia === v ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted"}`}>
            {v === 0 ? "Exato (R$ 0,00)" : reais(v)}
          </button>
        ))}
      </div>
      <form className="mt-3 flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); salvar(campo); }}>
        <span className="text-sm text-muted-foreground">Outro valor: R$</span>
        <Input value={campo} onChange={(e) => setCampo(e.target.value.replace(/[^\d,.]/g, ""))} inputMode="decimal"
          className="w-28 tabular-nums" disabled={tolerancia == null} data-e2e="caixa-tolerancia-campo" aria-label="Diferença aceitável em reais" />
        <Button type="submit" size="sm" disabled={tolerancia == null || salvando} data-e2e="caixa-tolerancia-salvar">Salvar</Button>
      </form>
    </BlocoRecolhivel>
  );
}
